import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { BRAND } from "@/data/site";

const links = [
  { to: "/", label: "Home", end: true, id: "nav-home-link" },
  { to: "/projects", label: "Projects", id: "nav-projects-link" },
  { to: "/services", label: "Services", id: "nav-services-link" },
  { to: "/gallery", label: "Gallery", id: "nav-gallery-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export function PublicLayout({ children, overlayHeader = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!overlayHeader) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlayHeader]);

  const overlay = overlayHeader && !scrolled;

  const headerCls = overlay
    ? "fixed inset-x-0 top-0 z-40 bg-transparent"
    : `${overlayHeader ? "fixed inset-x-0" : "sticky"} top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-md shadow-sm`;

  const linkCls = (isActive) =>
    overlay
      ? `px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive ? "text-teal" : "text-white/90 hover:text-teal"}`
      : `px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive ? "text-teal" : "text-navy hover:text-teal"}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className={headerCls}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-20 items-center">
          <motion.div
            className="w-[20%] min-w-[140px]"
            initial={overlayHeader ? { opacity: 0, x: -40 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link to="/" data-testid="nav-logo-link"><Logo onDark={overlay} /></Link>
          </motion.div>
          <motion.nav
            className="hidden md:flex w-[80%] items-center justify-end gap-1"
            initial={overlayHeader ? { opacity: 0, x: 40 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.id} className={({ isActive }) => linkCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
          </motion.nav>
          <button className={`md:hidden ml-auto ${overlay ? "text-white" : "text-navy"}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className={`md:hidden border-t px-4 py-3 space-y-1 ${overlay ? "border-white/10 bg-navy-void/95 backdrop-blur-md" : "border-slate-200 bg-white"}`}>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                className={`block px-3 py-2 rounded-md font-semibold ${overlay ? "text-white/90 hover:text-teal" : "text-navy hover:text-teal"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-navy-void text-white/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo onDark />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
              Premium residences engineered for elite homeowners and NRIs. Transparency, craftsmanship and on-time handover — always.
            </p>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="mt-5 inline-block text-teal font-semibold text-sm">
              NRI Investor Consultation →
            </a>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-3">Explore</h4>
            <ul className="space-y-2 text-sm">
              {links.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-teal">{l.label}</Link></li>)}
              <li><Link to="/admin/login" className="hover:text-teal">Admin Login</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-3">Contact</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li>{BRAND.address}</li>
              <li><a href={`tel:${BRAND.phone}`} className="hover:text-teal">{BRAND.phone}</a></li>
              <li><a href={`mailto:${BRAND.email}`} className="hover:text-teal">{BRAND.email}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
          © {new Date().getFullYear()} VG Homes. All rights reserved.
        </div>
      </footer>

      <WhatsAppButton />
    </div>
  );
}
